import moment from "moment";
import * as Yup from "yup";

import { ICustomerPlan } from "../../../../../../api/accounts/index.types";
import { CENTS_IN_DOLLAR, DATE_FORMAT } from "../../../../../../constants";

interface DIscountStrategy {
  apply(price: number): number;
}
class NoDiscount implements DIscountStrategy {
  apply(price: number): number {
    return price;
  }
}
class PercentDiscount implements DIscountStrategy {
  constructor(private percent: number) {}
  apply(price: number): number {
    return price - (price * this.percent) / CENTS_IN_DOLLAR;
  }
}
class FlatDiscount implements DIscountStrategy {
  constructor(private amount: number) {}
  apply(price: number): number {
    return price - this.amount;
  }
}
export interface ExpirationData {
  expirationDate: string;
  isOutExpiration: boolean;
  isNoOutExpiration: boolean;
  isHasExpiration: boolean;
}
interface ExpirationService {
  getExpirationData(plan: ICustomerPlan): ExpirationData;
}

export class DefaultExpirationService implements ExpirationService {
  getExpirationData(plan: ICustomerPlan): ExpirationData {
    const expirationDiscountDate = plan.discount_code?.expiration;
    const isHasExpiration = !!expirationDiscountDate;

    const isUseDateExpiration = isNaN(parseInt(expirationDiscountDate as string));
    const isUseNumberDaysExpiration = isHasExpiration && !isUseDateExpiration;
    const expirationDate = isUseNumberDaysExpiration
      ? moment(plan.start).add(expirationDiscountDate, "days")
      : moment(expirationDiscountDate);
    const isOutExpiration =
      isHasExpiration && expirationDate >= moment(plan.start).add(1, "month").subtract(1, "day");
    const isNoOutExpiration =
      isHasExpiration && expirationDate <= moment(plan.start).add(1, "month");

    return {
      expirationDate: moment(expirationDate).format(DATE_FORMAT),
      isOutExpiration,
      isNoOutExpiration,
      isHasExpiration,
    };
  }
}
class PlanPriceCalculator {
  constructor(
    private expirationService: ExpirationService,
    private discountStrategy: DIscountStrategy
  ) {}

  calculate(plan: ICustomerPlan, billing_history: { price_usd: number }): number {
    const originalPrice = plan.plan?.price_usd ?? plan.price_usd;
    const currentPrice = plan.price_usd / CENTS_IN_DOLLAR;
    const isPlanPriceChanged = plan.price_usd !== billing_history.price_usd;

    const {
      isOutExpiration,
      // isNoOutExpiration,
      isHasExpiration,
    } = this.expirationService.getExpirationData(plan);

    if (isHasExpiration && isPlanPriceChanged) {
      return currentPrice;
    }

    if (isOutExpiration) {
      return currentPrice;
    }

    if (isPlanPriceChanged) {
      return currentPrice;
    }

    // if (isNoOutExpiration) {
    //   return this.discountStrategy.apply(originalPrice) / CENTS_IN_DOLLAR;
    // }

    return this.discountStrategy.apply(originalPrice) / CENTS_IN_DOLLAR;
  }
}

export class CurrentPlanPrice {
  private calculator: PlanPriceCalculator;

  constructor(private plan: ICustomerPlan, private billing_history: { price_usd: number }) {
    // (DIP)
    const expirationService = new DefaultExpirationService();
    const discountStrategy = this.resolveDiscountStrategy(plan.discount_code);

    this.calculator = new PlanPriceCalculator(expirationService, discountStrategy);
  }

  private resolveDiscountStrategy(discount_code: ICustomerPlan["discount_code"]): DIscountStrategy {
    if (!discount_code?.amt || !discount_code?.type) return new NoDiscount();
    if (discount_code.type === "percent") return new PercentDiscount(Number(discount_code.amt));
    if (discount_code.type === "flat") return new FlatDiscount(Number(discount_code.amt));

    return new NoDiscount();
  }

  calculate(): number {
    return this.calculator.calculate(this.plan, this.billing_history);
  }
}

export const PLAN_VALIDATION_SCHEMA = Yup.object().shape({
  plans: Yup.array().of(
    Yup.object().shape({
      price_usd: Yup.number().required("Required"),
      expires: Yup.date().required("Start date is required").typeError("Invalid date"),
    })
  ),
});
